import React, { useEffect, useState } from 'react';
import { Grid, Box, IconButton, Tooltip } from '@mui/material';
import {
    ContentCopyOutlined,
    InsertPhotoOutlined,
    PictureAsPdfOutlined
} from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MetadataComponent from '../../components/misc/MetadataComponent';
import MarkdownContent from '../../components/misc/StyledMarkdown';
import QuillEditor from '../../components/misc/QuillEditor';
import { copyToClipboard, handleMarkdownConversion } from '../../utils/commonUtils';
import { useMarkdownToImage, useMarkdownToPDF } from '../../hooks/dataHooks';
import { marked } from 'marked';

const BookmarkCard = ({ bookmark }) => {
    const data = bookmark.data;
    const title = bookmark?.metadata?.title;
    const metaData = {
        subject: bookmark?.metadata?.subject,
        summary: bookmark?.metadata?.summary,
        description: bookmark?.metadata?.description,
        category: bookmark?.metadata?.category,
        content: bookmark?.metadata?.content,
        tags: bookmark?.metadata?.tags,
        keywords: bookmark?.metadata?.keywords,
    };

    const [open, setOpen] = useState(false);
    const [content, setContent] = useState('');
    const [editorTooltipOpen, setEditorTooltipOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
        setEditorTooltipOpen(false);
    };
    const handleClose = () => setOpen(false);

    const handleSave = (savedContent) => {
        setContent(savedContent);
        setOpen(false);
    };

    useEffect(() => {
        if (data) {
            const htmlContent = marked(data);
            setContent(htmlContent);
        }
    }, [data]);

    const { mutate: markdownToImage, isPending: isMarkdownToImagePending } = useMarkdownToImage();
    const { mutate: markdownToPDF, isPending: isMarkdownToPDFPending } = useMarkdownToPDF();

    const handleCopyMarkdown = ({ dataItem }) => {
        if (dataItem?.data) {
            copyToClipboard(dataItem.data); // Copy markdown text to clipboard
        }
    };

    const handleMarkdownToImage = (data) => {
        const dataItem = { response_data: data };
        handleMarkdownConversion(markdownToImage, 'png', dataItem);
    };

    const handleMarkdownToPDF = (data) => {
        const dataItem = { response_data: data };
        handleMarkdownConversion(markdownToPDF, 'pdf', dataItem);
    };

    return (
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Box
                sx={{
                    borderRadius: "8px 8px 0px 0px",
                    borderColor: 'divider',
                    height: '100%',
                    position: 'relative',
                    backgroundColor: "background.paper",
                    marginBottom: 4,
                }}
            >
                <MetadataComponent metaData={metaData} title={title} />

                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        borderRadius: "0px 0px 8px 8px",
                        padding: 1,
                        border: 1,
                        borderColor: 'divider',
                    }}
                >
                    <MarkdownContent data={data} />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            right: 8,
                            // opacity: 0,
                            transition: 'opacity 0.3s',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-end',
                            }}
                        >
                            {/* Copy button */}
                            <Tooltip title="Copy" arrow>
                                <IconButton
                                    size="small"
                                    sx={{ color: "secondary.light" }}
                                    onClick={() => handleCopyMarkdown({ dataItem: { data: data } })}
                                >
                                    <ContentCopyOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {/* Save button */}
                            <Tooltip title="Image" arrow>
                                <IconButton size="small"
                                    // onClick={() => captureAsImage({ dataItem: { data: data } })}
                                    onClick={() => { handleMarkdownToImage(data); }}
                                    sx={{ color: "secondary.main" }}>
                                    <InsertPhotoOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {/* Print button */}
                            <Tooltip title="PDF" arrow>
                                <IconButton size="small"
                                    // onClick={() => requestPDFDownload({ dataItem: { data: data } })}
                                    onClick={() => { handleMarkdownToPDF(data); }}
                                    sx={{ color: "secondary.dark" }}>
                                    <PictureAsPdfOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {/* Edit button */}
                            <Tooltip title="Edit" arrow open={editorTooltipOpen}>
                                <IconButton
                                    size="small"
                                    onClick={handleOpen}
                                    sx={{ color: 'secondary.dark' }}
                                // onMouseEnter={() => setEditorTooltipOpen(true)} // Show tooltip on hover
                                // onMouseLeave={() => setEditorTooltipOpen(false)} // Hide tooltip when hover is removed
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>

                            {/* QuillEditor popup */}
                            <QuillEditor
                                mode="popup"
                                open={open}
                                onClose={handleClose}
                                onSave={handleSave}
                                content={content}
                                setContent={setContent}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Grid>
    );
};

export default BookmarkCard;
